import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Icon,
  Image,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import { BathIcon, BedSingleIcon } from 'lucide-react';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';
import {
  getListingPrice,
  getPropertySource,
} from '~/apps/consumer/utils/listing.utils';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { formatToCurrency } from '~/services/document/utils/number';
import { CompsMap } from '../../Comparables/ViewComps/CompsMap/CompsMap';
import { CompsSummary } from '../../Comparables/ViewComps/CompsSummary';
import { getPropertyDetails } from '../../Listings/ListingPage/utils';
import { useBuyerOfferAnalysisContext } from '../Context/BuyerOfferAnalysisContext';

export const CompsCard = () => {
  const {
    comps,
    comparableSet,
    listing,
    comparableSetLoading,
    onEditCompsModalOpen,
    onViewCompsModalOpen,
    isViewOnly,
  } = useBuyerOfferAnalysisContext();
  const isMobile = useIsMobile();

  const onClick = (comp?: ComparableState) => {
    if (isViewOnly) {
      onViewCompsModalOpen();
    } else {
      onEditCompsModalOpen(comp);
    }
  };

  if (!listing || comparableSetLoading) {
    return <Skeleton flexBasis={0} flexGrow={3} height="250px" />;
  }

  return (
    <Card flexBasis={0} flexGrow={3} height={{ base: 'auto', md: '250px' }}>
      <CardHeader>
        <Flex alignItems="center" gap={2} justifyContent="space-between">
          <Text color="whiteAlpha.600">Comps</Text>
          <Button size="xs" variant="rounded-outline" onClick={() => onClick()}>
            {isViewOnly ? 'View Comps' : 'Edit Comps'}
          </Button>
        </Flex>
      </CardHeader>
      <CardBody pr={0}>
        <Flex
          alignItems={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap={2}
          height="100%"
          minWidth={0}
          overflow={{ base: 'unset', md: 'auto' }}
        >
          <Flex
            justifyContent="space-between"
            pr={{ base: 4, md: 0 }}
            width={{ base: '100%', md: 'unset' }}
          >
            <Flex direction="column" gap={2} minWidth="120px" width="120px">
              <CompsSummary
                compact
                comparableSet={comparableSet}
                listing={listing}
              />
            </Flex>
            <Flex
              height={{ base: '100px', md: '140px' }}
              minWidth={{ base: '160px', md: '220px' }}
              width={{ base: '160px', md: '220px' }}
              onClick={() => onClick()}
            >
              <CompsMap
                isMini
                comparableSet={comparableSet}
                mainListing={getPropertyDetails(listing)}
              />
            </Flex>
          </Flex>
          <Divider orientation={isMobile ? 'horizontal' : 'vertical'} />
          <Flex
            alignItems="center"
            gap={4}
            height="100%"
            overflow={{ base: 'auto', md: 'unset' }}
            width="100%"
          >
            {comps?.map((comp) => (
              <Flex
                key={comp.mlsListing?.id}
                _after={{
                  content: '""',
                  position: 'absolute',
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.6) 100%)',
                  zIndex: 1,
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
                cursor="pointer"
                direction="column"
                minHeight={{ base: '100px', md: '140px' }}
                minWidth={{ base: '160px', md: '220px' }}
                position="relative"
                onClick={() => onClick(comp)}
              >
                <Image
                  key={comp.mlsListing?.id}
                  borderRadius="6px"
                  height={{ base: '100px', md: '140px' }}
                  src={getPropertySource(comp.mlsListing?.photos?.[0])}
                  width={{ base: '160px', md: '220px' }}
                />
                <Flex
                  bottom="0"
                  direction="column"
                  overflow="hidden"
                  p={2}
                  position="absolute"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  zIndex={2}
                >
                  <Text fontSize={{ base: 'sm', md: 'md' }}>
                    {comp.mlsListing?.address?.full}
                  </Text>
                  <Flex alignItems="center" gap={2}>
                    <Text fontSize={{ base: 'sm', md: 'md' }}>
                      {formatToCurrency(getListingPrice(comp.mlsListing))}
                    </Text>
                    <Flex alignItems="center" gap={1}>
                      <Text fontSize={{ base: 'sm', md: 'md' }}>
                        {comp.mlsListing?.property?.bedrooms}
                      </Text>
                      <Icon
                        as={BedSingleIcon}
                        boxSize={{ base: '14px', md: '16px' }}
                      />
                    </Flex>
                    <Flex alignItems="center" gap={1}>
                      <Text fontSize={{ base: 'sm', md: 'md' }}>
                        {comp.mlsListing?.property?.bathrooms}
                      </Text>
                      <Icon
                        as={BathIcon}
                        boxSize={{ base: '14px', md: '16px' }}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};
